/** Pill + popover: provider / model / API key / thinking / threshold. */
import React, { useMemo, useState } from 'react';
import { Input, Popover, Select, Slider, Switch } from 'antd';
import { DownOutlined } from '@ant-design/icons';
import type { AiHubProvider } from '@/types/ai-agent';
import type { ChatLlmConfig } from './useAiChat';
import styles from './AiChatView.module.css';

const BASE_URL_PROVIDERS = new Set(['ollama', 'openai', 'openai_compat']);

/** Pill chỉ hiện model (nhà cung cấp xem trong popover). */
function pillLabel(cfg: ChatLlmConfig, providers: AiHubProvider[]) {
  const p = providers.find((x) => x.id === cfg.provider);
  const model = cfg.model.trim() || p?.default_model || p?.label || cfg.provider || 'AI';
  return model.length > 22 ? `${model.slice(0, 20)}…` : model;
}

export interface ChatLlmConfigPillProps {
  value: ChatLlmConfig;
  onChange: (next: ChatLlmConfig) => void;
  providers: AiHubProvider[];
  tenantHasApiKey?: boolean;
  tenantApiKeyMasked?: string | null;
  catalogLoading?: boolean;
  disabled?: boolean;
}

export const ChatLlmConfigPill: React.FC<ChatLlmConfigPillProps> = ({
  value,
  onChange,
  providers,
  tenantHasApiKey,
  tenantApiKeyMasked,
  catalogLoading,
  disabled,
}) => {
  const [open, setOpen] = useState(false);
  const label = useMemo(() => pillLabel(value, providers), [value, providers]);

  const currentProvider = useMemo(
    () => providers.find((p) => p.id === value.provider),
    [providers, value.provider],
  );

  const modelOptions = useMemo(
    () => (currentProvider?.models ?? []).map((m) => ({ value: m, label: m })),
    [currentProvider],
  );

  const showBaseUrl = value.provider && BASE_URL_PROVIDERS.has(value.provider);

  const content = (
    <div className={styles.llmPopover}>
      <Select
        showSearch
        optionFilterProp="label"
        loading={catalogLoading && !providers.length}
        disabled={disabled || !providers.length}
        placeholder="Nhà cung cấp"
        value={value.provider || undefined}
        options={providers.map((p) => ({
          value: p.id,
          label: p.id === 'ollama' && p.installed === false ? `${p.label} (offline)` : p.label,
        }))}
        onChange={(provider) => {
          const meta = providers.find((x) => x.id === provider);
          onChange({ ...value, provider, model: meta?.default_model ?? '', apiKey: '' });
        }}
        aria-label="Nhà cung cấp LLM"
      />

      <Select
        showSearch
        allowClear
        disabled={disabled || !currentProvider}
        placeholder={currentProvider?.default_model || 'Model mặc định'}
        value={value.model || undefined}
        options={modelOptions}
        onChange={(model) => onChange({ ...value, model: model ?? '' })}
        aria-label="Model LLM"
      />

      {currentProvider?.needs_api_key ? (
        <Input.Password
          autoComplete="new-password"
          disabled={disabled}
          value={value.apiKey}
          placeholder={
            tenantHasApiKey
              ? `API key — để trống dùng key đã lưu${tenantApiKeyMasked ? ` (${tenantApiKeyMasked})` : ''}`
              : 'API key (chỉ dùng cho phiên này)'
          }
          onChange={(e) => onChange({ ...value, apiKey: e.target.value })}
          aria-label="API key"
        />
      ) : null}

      {showBaseUrl ? (
        <Input
          disabled={disabled}
          value={value.baseUrl}
          placeholder="Base URL (tuỳ chọn)"
          onChange={(e) => onChange({ ...value, baseUrl: e.target.value })}
          aria-label="Base URL"
        />
      ) : null}

      <label className={styles.llmPopoverRow} title="Bật reasoning (Qwen / Ollama)">
        <span>Thinking</span>
        <Switch
          size="small"
          checked={value.thinking}
          disabled={disabled}
          onChange={(thinking) => onChange({ ...value, thinking })}
          aria-label="Bật thinking"
        />
      </label>

      <div title="Thấp = ổn định hơn · Cao = đa dạng hơn">
        <div className={styles.llmPopoverRow}>
          <span>Threshold</span>
          <strong>{value.threshold.toFixed(1)}</strong>
        </div>
        <Slider
          min={0}
          max={2}
          step={0.1}
          value={value.threshold}
          disabled={disabled}
          tooltip={{ open: false }}
          onChange={(threshold) => onChange({ ...value, threshold })}
          className={styles.llmPopoverSlider}
          aria-label="Threshold temperature"
        />
      </div>
    </div>
  );

  return (
    <Popover
      trigger="click"
      placement="topLeft"
      open={open}
      onOpenChange={setOpen}
      content={content}
      arrow={false}
      overlayClassName={styles.llmPopoverOverlay}
    >
      <button
        type="button"
        className={`${styles.llmPill} ${value.thinking ? styles.llmPillActive : ''}`}
        disabled={disabled}
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-label={`Cấu hình model: ${label}`}
      >
        <span className={styles.llmPillText}>{label}</span>
        <DownOutlined className={styles.llmPillCaret} aria-hidden />
      </button>
    </Popover>
  );
};

export default ChatLlmConfigPill;
